'use client';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { leaderboardApi } from '@/services/api';
import { LeaderboardQueryParams, LeaderboardTrader, MarketClosedPosition } from '@/types';

/**
 * Common query keys for the leaderboard module.
 * Using a central object for keys ensures consistency across the app.
 */
export const LEADERBOARD_QUERY_KEYS = {
  all: ['leaderboard'] as const,
  lists: () => [...LEADERBOARD_QUERY_KEYS.all, 'list'] as const,
  list: (params: LeaderboardQueryParams) => [...LEADERBOARD_QUERY_KEYS.lists(), params] as const,
  categories: () => [...LEADERBOARD_QUERY_KEYS.all, 'categories'] as const,
  timePeriods: () => [...LEADERBOARD_QUERY_KEYS.all, 'timePeriods'] as const,
  biggestWinners: (params: LeaderboardQueryParams) =>
    [...LEADERBOARD_QUERY_KEYS.all, 'biggest-winners', params] as const,
  traderStats: (address: string) =>
    [...LEADERBOARD_QUERY_KEYS.all, 'trader-stats', address] as const,
  userValue: (address: string) => [...LEADERBOARD_QUERY_KEYS.all, 'user-value', address] as const,
  userStats: (address: string) => [...LEADERBOARD_QUERY_KEYS.all, 'user-stats', address] as const,
  publicProfile: (address: string, params?: any) =>
    [...LEADERBOARD_QUERY_KEYS.all, 'public-profile', address, params] as const,
  activity: (address: string) => [...LEADERBOARD_QUERY_KEYS.all, 'activity', address] as const,
  positions: (params: object) => [...LEADERBOARD_QUERY_KEYS.all, 'positions', params] as const,
  closedPositions: (params: object) =>
    [...LEADERBOARD_QUERY_KEYS.all, 'closed-positions', params] as const,
};

/**
 * Fetch leaderboard data with infinite pagination.
 * @param params Query parameters for filtering and sorting.
 */
export const useLeaderboardData = (params?: LeaderboardQueryParams) => {
  const limit = params?.limit || 20;

  return useInfiniteQuery({
    queryKey: LEADERBOARD_QUERY_KEYS.list(params || {}),
    queryFn: ({ pageParam = 0 }) =>
      leaderboardApi.getLeaderboard({
        ...params,
        offset: pageParam,
        limit,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < limit) return undefined;
      return allPages.length * limit;
    },
    initialPageParam: 0,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch available leaderboard categories.
 */
export const useLeaderboardCategories = <TData = string[]>(select?: (data: string[]) => TData) => {
  return useQuery({
    queryKey: LEADERBOARD_QUERY_KEYS.categories(),
    queryFn: () => leaderboardApi.getCategories(),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    select,
  });
};

/**
 * Fetch available leaderboard time periods.
 */
export const useLeaderboardTimePeriods = <TData = string[]>(select?: (data: string[]) => TData) => {
  return useQuery({
    queryKey: LEADERBOARD_QUERY_KEYS.timePeriods(),
    queryFn: () => leaderboardApi.getTimePeriods(),
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    select,
  });
};

/**
 * Fetch biggest winners data.
 */
export const useBiggestWinners = <TData = LeaderboardTrader[]>(
  params?: LeaderboardQueryParams,
  select?: (data: LeaderboardTrader[]) => TData
) => {
  return useQuery({
    queryKey: LEADERBOARD_QUERY_KEYS.biggestWinners(params || {}),
    queryFn: () => leaderboardApi.getBiggestWinners(params),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    select,
  });
};

/**
 * Fetch stats for a specific trader.
 */
export const useTraderStats = (
  params: LeaderboardQueryParams & { user: string },
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: LEADERBOARD_QUERY_KEYS.traderStats(params.user + JSON.stringify(params)),
    queryFn: async () => {
      const data = await leaderboardApi.getLeaderboard({ ...params, limit: 1 });
      return data?.[0] || null;
    },
    enabled: !!params.user && options?.enabled,
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
  });
};

/**
 * Fetch monetary value for a user.
 */
export const useUserValue = (userAddress: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: LEADERBOARD_QUERY_KEYS.userValue(userAddress),
    queryFn: () => leaderboardApi.getUserValue(userAddress),
    enabled: !!userAddress && options?.enabled,
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
  });
};

/**
 * Fetch stats for a user (trades, largestWin, views, position value).
 */
export const useUserStats = (userAddress: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: LEADERBOARD_QUERY_KEYS.userStats(userAddress),
    queryFn: () => leaderboardApi.getUserStats(userAddress),
    enabled: !!userAddress && options?.enabled,
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
  });
};

/**
 * Fetch public profile for a user address with sorting and filtering.
 */
export const usePublicProfile = (
  userAddress: string,
  params?: any,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: LEADERBOARD_QUERY_KEYS.publicProfile(userAddress, params),
    queryFn: () => leaderboardApi.getPublicProfile(userAddress, params),
    enabled: !!userAddress && options?.enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch sort categories for active or closed positions.
 * @param type The type of positions (active or closed).
 */
export const useLeaderboardSortCategories = (type: 'active-positions' | 'closed-positions') => {
  return useQuery({
    queryKey: [...LEADERBOARD_QUERY_KEYS.all, 'sortCategories', type],
    queryFn: () => leaderboardApi.getSortCategories(type),
    staleTime: Infinity,
    gcTime: Infinity,
  });
};

/**
 * Fetch user activity from markets/activity endpoint.
 */
export const useUserActivity = (userAddress: string, options?: { enabled?: boolean }) => {
  return useInfiniteQuery({
    queryKey: LEADERBOARD_QUERY_KEYS.activity(userAddress),
    queryFn: ({ pageParam = 0 }) =>
      leaderboardApi.getUserActivity(userAddress, 10, pageParam as number),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer than 10 items, there are no more pages
      return lastPage.length === 10 ? allPages.length * 10 : undefined;
    },
    enabled: !!userAddress && options?.enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch user positions from markets/positions endpoint.
 */
export const useUserPositions = (
  params: {
    user: string;
    title?: string;
    sizeThreshold?: number;
    sortBy?: string;
    sortDirection?: string;
  },
  options?: { enabled?: boolean }
) => {
  return useInfiniteQuery({
    queryKey: [...LEADERBOARD_QUERY_KEYS.all, 'positions', params],
    queryFn: ({ pageParam = 0 }) =>
      leaderboardApi.getUserPositions({ ...params, limit: 30, offset: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.length === 30 ? allPages.length * 30 : undefined;
    },
    enabled: !!params.user && options?.enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Fetch user closed positions from markets/closed-positions endpoint.
 */
export const useUserClosedPositions = (
  params: {
    user: string;
    title?: string;
    sortBy?: string;
    sortDirection?: string;
  },
  options?: { enabled?: boolean }
) => {
  return useInfiniteQuery({
    queryKey: LEADERBOARD_QUERY_KEYS.closedPositions(params),
    queryFn: ({ pageParam = 0 }) =>
      leaderboardApi.getUserClosedPositions({ ...params, limit: 30, offset: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: MarketClosedPosition[], allPages: MarketClosedPosition[][]) => {
      return lastPage.length === 30 ? allPages.length * 30 : undefined;
    },
    enabled: !!params.user && options?.enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};
